import axios from 'axios'
import { getAccessToken } from '@/helpers/utils'
import { CONFIG } from '@/const/config'
import { piniaHasPortalRight } from '@/helpers/portalPermissions'
import { B2bPortalRight } from '@/enums/B2bPortalRight'

// Resolve baseURL: use VUE_APP_API_URL if set, otherwise '' (for webpack proxy on localhost)
// Matches interceptors.ts pattern for consistent API routing
function getTradingBaseURL(): string {
  try {
    const mode = localStorage.getItem('dev_api_mode')
    if (mode === 'remote') return 'https://business.dev.nb2.tech'
  } catch { /* SSR or no localStorage */ }
  // CONFIG.VUE_APP_API_URL defaults to '' when not set (webpack proxy), or uses explicit value when set
  return CONFIG.VUE_APP_API_URL
}

// Trading uses its own clean axios instance — no global 401-redirect interceptor.
// Uses the same baseURL as the main app so API calls reach the backend on all environments.
const tradingHttp = axios.create({
  baseURL: getTradingBaseURL(),
  headers: {
    'x-api-key': CONFIG.VUE_APP_X_API_KEY,
  },
})

tradingHttp.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

// ── Re-export types (same shape as tradingMock.ts) ──

export interface TradingPair {
  name: string
  urlSymbol: string
  base: string
  counter: string
  baseDecimals: number
  counterDecimals: number
  // Precision used for instant (market) orders on the counter currency —
  // backend exposes this separately because e.g. BTC/USD has counterDecimals=0
  // (raw USD) but instantOrderCounterDecimals=2 (cents).
  instantOrderCounterDecimals?: number
  lastPrice: number
  change24h: number | null  // null = not yet loaded, number = real value
  minimumOrderAmount: number
  minimumOrderValue: number
  maximumOrderAmount: number
  maximumOrderValue: number
  bitstamp: boolean
  bitgo: boolean
  // Whether instant/market orders are allowed for this pair. Derived from the
  // backend `instantAndMarketOrders` flag ("Enabled"/"Disabled"). Limit orders
  // are allowed for every tradable pair, so there's no separate `supportsLimit`.
  // Some pairs (e.g. stablecoin USDC/USDT) are limit-only — a Market dry-run on
  // them is rejected by the backend, so callers must gate Market on this flag.
  // Optional: when absent (e.g. the ExchangeCard fallback pair) treat as allowed
  // — only an explicit `false` disables Market.
  supportsMarket?: boolean
}

export interface Ticker {
  last: string
  bid: string
  ask: string
  high: string
  low: string
  open: string
  volume: string
  vwap: string
  percentChange24: string
}

export interface OrderBookEntry {
  price: string
  amount: string
}

export interface OrderBook {
  bids: OrderBookEntry[]
  asks: OrderBookEntry[]
}

export interface OHLCCandle {
  timestamp: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

export interface Trade {
  date: string
  tid: number
  price: string
  type: '0' | '1' // 0 = buy, 1 = sell
  amount: string
}

export interface TradeHistoryEntry {
  date: string
  pair: string
  type: string
  side: 'buy' | 'sell'
  price: string
  amount: string
  fee: string
  total: string
  status: string
  orderId: string
}

export type ChartInterval = '1H' | '1D' | '1W' | '1M' | '3M' | '6M' | '1Y'

export interface OpenOrder {
  date: string
  pair: string
  type: string
  side: 'buy' | 'sell'
  price: string
  amount: string
  filled: string
  total: string
  orderId: string
}

// ── Backend response types ──

interface ExchangeMarketModel {
  name: string
  marketSymbol: string
  baseCurrency: string
  baseDecimals: number
  counterCurrency: string
  counterDecimals: number
  instantOrderCounterDecimals?: number
  minimumOrderValue: number
  maximumOrderValue: number
  minimumOrderAmount: number
  maximumOrderAmount: number
  trading: boolean
  // "Enabled" / "Disabled" — controls whether Market/instant orders are allowed
  // for this pair (Bitstamp `instant_and_market_orders`). Limit is always allowed.
  instantAndMarketOrders?: string
  description: string
  marketType: string
  bitstamp: boolean
  bitgo: boolean
}

interface CurrentExchangeRateModel {
  currencyFrom: string
  currencyTo: string
  rate: number
  volume?: number
  high?: number
  low?: number
  open?: number
  percentChange24?: number
}

interface ExchangeRateHistoryModel {
  date: string
  rate: number
}

interface AggregatedExchangeRatesModel {
  rates1H: ExchangeRateHistoryModel[]
  rates1D: ExchangeRateHistoryModel[]
  rates1W: ExchangeRateHistoryModel[]
  rates1M: ExchangeRateHistoryModel[]
}

interface ExchangeTradeModel {
  date: string
  tid: number
  price: number
  amount: number
  type: string // 'buy' | 'sell' or similar
}

interface ExchangeOrderItemModel {
  orderDate: string
  orderAcceptedDate: string
  orderStatus: string
  referenceNumber: string
  externalReference: string
  currency: string
  amount: number
  buyCurrency: string
  buyAmount: number
  rate: number
  exchangeDirection: string // 'Buy' | 'Sell'
  exchangeType: string // 'Market' | 'Limit' | 'StopLimit'
  marketSymbol: string
  feeAmount: number
  feeCurrency: string
}

export interface DryRunResponse {
  debitedAmount: number
  creditedAmount: number
  systemFeeAmount: number
  providerFeeAmount: number
  debitedCurrency: string
  creditedCurrency: string
  systemFeeCurrency: string
  providerFeeCurrency: string
  price: number
}

interface CreateOrderResponse {
  referenceNumber: string
}

// ── SessionStorage Cache Layer ──
// Caches API responses so page revisits are instant while fresh data loads in background.

interface CacheEntry<T> {
  data: T
  ts: number
}

const CACHE_PREFIX = 'trading_'
const CACHE_TTL: Record<string, number> = {
  pairs: 5 * 60 * 1000,           // 5 min
  ticker: 3 * 1000,                // 3s (shorter than 5s refresh interval)
  orderBook: 3 * 1000,             // 3s (shorter than 5s refresh interval)
  ohlc: 2 * 60 * 1000,             // 2 min
  oracleRaw: 30 * 60 * 1000,       // 30 min (oracle data updates every 6 hours)
  aggregatedRates: 60 * 1000,      // 1 min — matches backend freshness
  recentTrades: 3 * 1000,          // 3s (shorter than 5s refresh interval)
  tradeHistory: 5 * 60 * 1000,     // 5 min (prefetched at app startup)
  openOrders: 5 * 60 * 1000,       // 5 min (prefetched at app startup)
}

function cacheGet<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key)
    if (!raw) return null
    const entry: CacheEntry<T> = JSON.parse(raw)
    const ttlKey = key.split(':')[0] // e.g. "ticker:btcusd" -> "ticker"
    const ttl = CACHE_TTL[ttlKey] || 30000
    if (Date.now() - entry.ts > ttl) return null
    return entry.data
  } catch { return null }
}

function cacheSet<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = { data, ts: Date.now() }
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry))
  } catch { /* storage full — ignore */ }
}

/** Invalidate order-related caches so next fetch hits the API */
export function invalidateOrderCache(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(CACHE_PREFIX) && (key.includes('tradeHistory') || key.includes('openOrders'))) {
        sessionStorage.removeItem(key)
      }
    }
  } catch { /* ignore */ }
}

/** Get stale cache (ignoring TTL) for instant display while fresh data loads */
function cacheGetStale<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key)
    if (!raw) return null
    const entry: CacheEntry<T> = JSON.parse(raw)
    return entry.data
  } catch { return null }
}

// ── Helpers ──

function formatPairName(base: string, counter: string): string {
  return `${base.toUpperCase()}/${counter.toUpperCase()}`
}

const KNOWN_COUNTERS = ['usdt', 'usdc', 'rlusd', 'eurc', 'eurcv', 'usd', 'eur', 'gbp', 'btc', 'eth']

function marketSymbolToUrlSymbol(marketSymbol: string): string {
  const s = marketSymbol.toLowerCase()
  for (const c of KNOWN_COUNTERS) {
    if (s.endsWith(c) && s.length > c.length) {
      return s.slice(0, -c.length) + '-' + c
    }
  }
  return s
}

function urlSymbolToMarketSymbol(urlSymbol: string): string {
  return urlSymbol.replace('-', '').toLowerCase()
}

/** Map interval label to approximate date range for rate history */
function intervalToDateRange(interval: ChartInterval): { dateFrom: string; dateTo: string } {
  const now = new Date()
  const dateTo = now.toISOString()
  const from = new Date(now)

  switch (interval) {
    case '1H': from.setHours(from.getHours() - 1); break
    case '1D': from.setHours(from.getHours() - 24); break  // Full 24h for maximum detail
    case '1W': from.setDate(from.getDate() - 7); break
    case '1M': from.setMonth(from.getMonth() - 1); break
    case '3M': from.setMonth(from.getMonth() - 3); break
    case '6M': from.setMonth(from.getMonth() - 6); break
    case '1Y': from.setFullYear(from.getFullYear() - 1); break
  }

  return { dateFrom: from.toISOString(), dateTo }
}

/** Expected candle count per interval (candle chart) */
const CANDLE_COUNTS: Record<ChartInterval, number> = {
  '1H': 20,  // 60min / 3min = 20 candles
  '1D': 24,  // 24h / 1h = 24 candles
  '1W': 28,  // 7d * 4 (6h candles) = 28 candles
  '1M': 30,  // 30d / 1d = 30 candles
  '3M': 50,
  '6M': 50,
  '1Y': 50,
}

/** Line chart: max points per interval (downsample if raw data exceeds this) */
const LINE_COUNTS: Record<ChartInterval, number> = {
  '1H': 60,
  '1D': 150,
  '1W': 120,
  '1M': 180,
  '3M': 90,
  '6M': 180,
  '1Y': 180,
}

/**
 * Remove stale data by finding large price cliffs (>10% single-tick jump)
 * and keeping only data after the last cliff.
 */
function removeStaleData(rates: ExchangeRateHistoryModel[]): ExchangeRateHistoryModel[] {
  if (rates.length < 2) return rates

  // Sort by date ascending
  const sorted = [...rates].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  // Find the last cliff (>10% jump between consecutive points)
  let lastCliffIdx = 0
  for (let i = 1; i < sorted.length; i++) {
    const change = Math.abs(sorted[i].rate - sorted[i - 1].rate) / sorted[i - 1].rate
    if (change > 0.10) {
      lastCliffIdx = i
    }
  }

  // Keep data from after the last cliff
  return lastCliffIdx > 0 ? sorted.slice(lastCliffIdx) : sorted
}

/**
 * Convert flat rate history points into OHLC candles.
 * Removes stale data first, then groups into time-based buckets.
 */
function ratesToOHLC(rates: ExchangeRateHistoryModel[], targetCount: number): OHLCCandle[] {
  if (rates.length === 0) return []

  // Remove stale data (old rates with cliff jumps)
  const sorted = removeStaleData(rates)

  // If fewer points than target candles, each point becomes a candle
  if (sorted.length <= targetCount) {
    return sorted.map((r) => {
      const ts = new Date(r.date).getTime()
      return {
        timestamp: ts,
        open: r.rate,
        high: r.rate,
        low: r.rate,
        close: r.rate,
        volume: 0,
      }
    })
  }

  // Group into time-based buckets
  const startTime = new Date(sorted[0].date).getTime()
  const endTime = new Date(sorted[sorted.length - 1].date).getTime()
  const timeRange = endTime - startTime
  const bucketDuration = timeRange / targetCount

  const candles: OHLCCandle[] = []
  let bucketStart = startTime
  let idx = 0

  for (let b = 0; b < targetCount && idx < sorted.length; b++) {
    const bucketEnd = bucketStart + bucketDuration
    const bucket: ExchangeRateHistoryModel[] = []

    while (idx < sorted.length && new Date(sorted[idx].date).getTime() < bucketEnd) {
      bucket.push(sorted[idx])
      idx++
    }

    // Last bucket: collect remaining points
    if (b === targetCount - 1) {
      while (idx < sorted.length) {
        bucket.push(sorted[idx])
        idx++
      }
    }

    if (bucket.length > 0) {
      const rateValues = bucket.map((r) => r.rate)
      candles.push({
        timestamp: new Date(bucket[0].date).getTime(),
        open: rateValues[0],
        high: Math.max(...rateValues),
        low: Math.min(...rateValues),
        close: rateValues[rateValues.length - 1],
        volume: 0,
      })
    }

    bucketStart = bucketEnd
  }

  return candles
}

/** Aggregate OHLC candles into fewer candles by grouping consecutive candles */
function aggregateCandles(candles: OHLCCandle[], targetCount: number): OHLCCandle[] {
  if (candles.length <= targetCount) return candles

  const groupSize = Math.ceil(candles.length / targetCount)
  const result: OHLCCandle[] = []

  for (let i = 0; i < candles.length; i += groupSize) {
    const group = candles.slice(i, i + groupSize)
    result.push({
      timestamp: group[0].timestamp,
      open: group[0].open,
      high: Math.max(...group.map((c) => c.high)),
      low: Math.min(...group.map((c) => c.low)),
      close: group[group.length - 1].close,
      volume: group.reduce((sum, c) => sum + c.volume, 0),
    })
  }

  return result
}

// ── Ticker update callback ──
// When background stats (volume, high, low) arrive after ticker already returned,
// call registered callback so UI can update without waiting for next refresh cycle.
type TickerUpdateCallback = (ticker: Ticker) => void
const tickerUpdateCallbacks = new Map<string, TickerUpdateCallback>()

export function onTickerUpdate(pair: string, cb: TickerUpdateCallback): void {
  tickerUpdateCallbacks.set(pair, cb)
}

export function offTickerUpdate(pair: string): void {
  tickerUpdateCallbacks.delete(pair)
}

// ── Instant stale data for pre-population ──
// Returns stale cache (ignoring TTL) so UI can show numbers immediately on mount.

export function getStaleData<T>(key: string): T | null {
  return cacheGetStale<T>(CACHE_PREFIX.length ? key : key)
}

// Convenience: get stale ticker/candles/orderBook for a pair
export function getStaleTicker(pair: string): Ticker | null {
  return cacheGetStale<Ticker>('ticker:' + pair)
}

export function getStaleCandles(pair: string, interval: ChartInterval): OHLCCandle[] | null {
  return cacheGetStale<OHLCCandle[]>('ohlc:' + pair + ':' + interval)
}

export function getStaleOrderBook(pair: string): OrderBook | null {
  return cacheGetStale<OrderBook>('orderBook:' + pair)
}

export function getStaleRecentTrades(pair: string): Trade[] | null {
  return cacheGetStale<Trade[]>('recentTrades:' + pair)
}

export function getStalePairs(): TradingPair[] | null {
  return cacheGetStale<TradingPair[]>('pairs')
}

export function getStaleTradeHistory(): TradeHistoryEntry[] | null {
  return cacheGetStale<TradeHistoryEntry[]>('tradeHistory')
}

export function getStaleOpenOrders(): OpenOrder[] | null {
  return cacheGetStale<OpenOrder[]>('openOrders')
}

// CCData removed — all Oracle data served from backend DB via API

// ── Pairs update callback ──
type PairsUpdateCallback = (pairs: TradingPair[]) => void
let pairsUpdateCallback: PairsUpdateCallback | null = null

export function onPairsUpdate(cb: PairsUpdateCallback): void {
  pairsUpdateCallback = cb
}

export function offPairsUpdate(): void {
  pairsUpdateCallback = null
}

// ── API Functions ──

export async function fetchTradingPairs(): Promise<TradingPair[]> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return []
  }
  // Return cached data instantly if fresh
  const cached = cacheGet<TradingPair[]>('pairs')
  if (cached && cached.length > 0) return cached

  try {
    const { data } = await tradingHttp.get('/api/crypto/exchange/markets', {
      timeout: 15000,
      params: { includeDisabled: false },
    })

    const rawMarkets: ExchangeMarketModel[] = data.markets || data || []

    // TODO: TEMPORARY — remove this filter once AVAX, BASE, DOT, POLYGON, ZEC pairs are re-enabled.
    // Hardcoded to hide markets where these currencies appear as base or counter.
    // ──────── BEGIN TEMP FILTER ────────
    const DISABLED_EXCHANGE_CURRENCIES = ['AVAX','AVAXC', 'BASE', 'DOT', 'POLYGON', 'ZEC']
    const markets: ExchangeMarketModel[] = rawMarkets.filter((m) => {
      const base = (m.baseCurrency || '').toUpperCase()
      const counter = (m.counterCurrency || '').toUpperCase()
      return !DISABLED_EXCHANGE_CURRENCIES.includes(base) && !DISABLED_EXCHANGE_CURRENCIES.includes(counter)
    })
    // ──────── END TEMP FILTER ────────

    // Preserve stale price/change data from previous cache so dropdown shows values instantly
    const stalePairs = cacheGetStale<TradingPair[]>('pairs')
    const staleMap = new Map<string, TradingPair>()
    if (stalePairs) {
      for (const sp of stalePairs) staleMap.set(sp.urlSymbol, sp)
    }

    // Sort by popularity: major coins first, then alphabetical
    const COIN_PRIORITY: Record<string, number> = {
      BTC: 1, ETH: 2, XRP: 3, LTC: 4, BCH: 5, LINK: 6, XLM: 7,
      AAVE: 8, UNI: 9, SOL: 10, DOT: 11, ADA: 12, AVAX: 13,
    }
    // USD pairs first within same base, then EUR, then others
    const COUNTER_PRIORITY: Record<string, number> = { USD: 1, EUR: 2, GBP: 3, BTC: 4 }

    // Deduplicate by name (API returns spot + perpetual with same display name)
    const seen = new Set<string>()
    const pairs: TradingPair[] = markets.map((m) => {
      const sym = marketSymbolToUrlSymbol(m.marketSymbol)
      const stale = staleMap.get(sym)
      return {
        name: formatPairName(m.baseCurrency, m.counterCurrency),
        urlSymbol: sym,
        base: m.baseCurrency.toUpperCase(),
        counter: m.counterCurrency.toUpperCase(),
        baseDecimals: m.baseDecimals,
        counterDecimals: m.counterDecimals,
        instantOrderCounterDecimals: m.instantOrderCounterDecimals,
        lastPrice: stale?.lastPrice || 0,
        change24h: null,
        minimumOrderAmount: m.minimumOrderAmount || 0,
        minimumOrderValue: m.minimumOrderValue || 0,
        maximumOrderAmount: m.maximumOrderAmount || 0,
        maximumOrderValue: m.maximumOrderValue || 0,
        bitstamp: m.bitstamp ?? false,
        bitgo: m.bitgo ?? false,
        // Market allowed only when the backend explicitly enables it. When the
        // flag is missing (older payloads) default to allowed to avoid hiding
        // Market for pairs that actually support it.
        supportsMarket: m.instantAndMarketOrders
          ? m.instantAndMarketOrders.trim().toLowerCase() === 'enabled'
          : true,
      }
    }).filter((p) => {
      if (seen.has(p.name)) return false
      seen.add(p.name)
      return true
    })

    cacheSet('pairs', pairs)

    // Enrich 24H-change in the background. Consumers that care about it
    // (TradingPairSelector, CryptoDetail) will see the value populate
    // reactively; the Exchange calculator on the dashboard doesn't use it
    // and should not wait on a slow/unavailable backend here.
    ;(async () => {
    try {
      const changesRes = await tradingHttp.get('/api/crypto/exchange/24h-changes', { timeout: 8000 })
      const changes: any[] = changesRes.data || []
      const changeMap = new Map<string, number>()
      for (const c of changes) {
        // ExchangeRatesHistory uses CurrSell=base, CurrBuy=counter (sell base to buy counter)
        changeMap.set(`${c.currSell}/${c.currBuy}`, c.changePct24h)
      }
      for (const p of pairs) {
        const pct = changeMap.get(`${p.base}/${p.counter}`)
        if (pct !== undefined && pct !== null) {
          p.change24h = pct
        }
      }
    } catch {
      // Fallback: calculate from aggregated rates (same source as ticker header)
      // Only fetch major base currencies to avoid overwhelming the API
      const STABLECOINS = new Set(['USD', 'USDC', 'USDT', 'EUR', 'GBP'])
      const MAJOR_BASES = ['BTC', 'ETH', 'XRP', 'LTC', 'BCH', 'LINK', 'XLM',
        'AAVE', 'UNI', 'SOL', 'DOT', 'ADA', 'AVAXC', 'COMP', 'GRT', 'ALGO', 'CRV']
      const pairBases = new Set(pairs.map(p => p.base))
      const basesToFetch = MAJOR_BASES.filter(b => pairBases.has(b))
      const baseChangeMap = new Map<string, number>()

      // Fetch in batches of 3 with delay to avoid server overload
      try {
        for (let i = 0; i < basesToFetch.length; i += 3) {
          if (i > 0) await new Promise(r => setTimeout(r, 300))
          const batch = basesToFetch.slice(i, i + 3)
          await Promise.all(batch.map(async (base) => {
            try {
              const pairKey = `${base.toLowerCase()}-usd`
              const aggregated = await fetchAggregatedRates(pairKey)
              if (aggregated?.rates1D?.length) {
                const rates1D = aggregated.rates1D
                const price24hAgo = rates1D[0]?.rate || 0
                const currentPrice = rates1D[rates1D.length - 1]?.rate || 0
                if (price24hAgo > 0 && currentPrice > 0) {
                  baseChangeMap.set(base, ((currentPrice - price24hAgo) / price24hAgo) * 100)
                }
              }
            } catch { /* skip */ }
          }))
        }
      } catch { /* aggregated rates unavailable */ }

      // Derive changes for all pairs from base/USD data
      const btcChange = baseChangeMap.get('BTC') || 0
      for (const p of pairs) {
        if (p.change24h !== null) continue
        const baseChange = baseChangeMap.get(p.base)
        if (baseChange === undefined) continue

        if (STABLECOINS.has(p.counter)) {
          // BASE/USD ≈ BASE/USDT ≈ BASE/USDC
          p.change24h = baseChange
        } else if (p.counter === 'BTC' && btcChange !== 0) {
          // BASE/BTC ≈ BASE/USD change - BTC/USD change
          p.change24h = baseChange - btcChange
        } else if (baseChangeMap.has(p.counter)) {
          // Cross-pair: BASE/COUNTER ≈ BASE/USD - COUNTER/USD
          p.change24h = baseChange - (baseChangeMap.get(p.counter) || 0)
        }
      }
    }

    // Re-sort with newly populated change24h so pairs with real data come
    // first. TradingPairSelector observes `pairs` reactively.
    pairs.sort((a, b) => {
      const aHas = a.change24h !== null && a.change24h !== 0
      const bHas = b.change24h !== null && b.change24h !== 0
      if (aHas && !bHas) return -1
      if (!aHas && bHas) return 1
      const ap = COIN_PRIORITY[a.base] || 99
      const bp = COIN_PRIORITY[b.base] || 99
      if (ap !== bp) return ap - bp
      const ac = COUNTER_PRIORITY[a.counter] || 99
      const bc = COUNTER_PRIORITY[b.counter] || 99
      return ac - bc
    })
    cacheSet('pairs', pairs)
    })().catch(() => { /* background enrichment never blocks callers */ })

    return pairs
  } catch (e) {
    console.error('[tradingApi] fetchTradingPairs failed:', e)
    return cacheGetStale<TradingPair[]>('pairs') || []
  }
}

// Cache 24h stats per pair to avoid re-fetching on every refresh
const cached24hStats: Record<string, { high: number; low: number; open: number; volume: number; ts: number }> = {}

export async function fetchTicker(pair: string): Promise<Ticker> {
  const marketSymbol = urlSymbolToMarketSymbol(pair)
  const defaultTicker: Ticker = {
    last: '0', bid: '0', ask: '0', high: '0', low: '0',
    open: '0', volume: '0', vwap: '0', percentChange24: '0',
  }

  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return defaultTicker
  }

  const cached = cacheGet<Ticker>('ticker:' + pair)
  if (cached) return cached

  try {
    const rateRes = await tradingHttp.get('/api/crypto/exchange/current-rate', {
      timeout: 15000,
      params: { marketSymbol },
    })

    const rate: CurrentExchangeRateModel = rateRes.data
    const last = rate.rate

    let bid = last * 0.9998
    let ask = last * 1.0002
    let high = last
    let low = last
    let open = last
    let volume = 0
    let percentChange = 0

    // Calculate 24h change from aggregated rate history (reliable, same source as chart)
    try {
      const aggregated = await fetchAggregatedRates(pair)
      if (aggregated?.rates1D?.length) {
        const rates1D = aggregated.rates1D
        const price24hAgo = rates1D[0]?.rate || 0
        if (price24hAgo > 0 && last > 0) {
          open = price24hAgo
          percentChange = ((last - price24hAgo) / price24hAgo) * 100
        }
        // Derive high/low from 1D data
        const allRates = rates1D.map((r: any) => r.rate).filter((r: number) => r > 0)
        if (allRates.length > 0) {
          high = Math.max(...allRates, last)
          low = Math.min(...allRates, last)
        }
      }
    } catch { /* aggregated rates unavailable */ }

    // Supplement with oracle data (volume, mktCap — fields not in rate history)
    try {
      const oracleRes = await tradingHttp.get('/api/crypto/exchange/oracle-rates', { timeout: 5000 })
      const oracleRates: any[] = oracleRes.data || []
      const ms = marketSymbol.toLowerCase()
      const oracle = oracleRates.find((r: any) =>
        (r.currBuy + r.currSell).toLowerCase() === ms ||
        (r.currBuy?.toLowerCase() + r.currSell?.toLowerCase()) === ms
      )
      if (oracle) {
        if (oracle.volume > 0) volume = oracle.volume
      }
    } catch { /* Oracle unavailable */ }

    // Background: refresh Bitstamp rate history for chart (not for ticker stats)
    const needsBackgroundRefresh = !cached24hStats[marketSymbol] || Date.now() - cached24hStats[marketSymbol].ts > 60000
    if (needsBackgroundRefresh && piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
      tradingHttp.get('/api/crypto/exchange/rates', {
        timeout: 15000,
        params: {
          marketSymbol,
          dateFrom: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          dateTo: new Date().toISOString(),
        },
      }).then((histRes) => {
        let h = last, l = last, o = last
        if (histRes?.data) {
          const cleaned = removeStaleData(histRes.data)
          if (cleaned.length > 0) {
            const rateValues = cleaned.map((r: ExchangeRateHistoryModel) => r.rate)
            h = Math.max(...rateValues)
            l = Math.min(...rateValues)
            o = rateValues[0]
          }
        }
        cached24hStats[marketSymbol] = { high: h, low: l, open: o, volume, ts: Date.now() }
      }).catch(() => {})
    }

    const vwap = (high + low) / 2
    const result: Ticker = {
      last: last.toString(),
      bid: bid.toString(),
      ask: ask.toString(),
      high: high.toString(),
      low: low.toString(),
      open: open.toString(),
      volume: volume.toFixed(2),
      vwap: vwap.toString(),
      percentChange24: percentChange.toFixed(2),
    }
    cacheSet('ticker:' + pair, result)
    return result
  } catch (e) {
    console.error('[tradingApi] fetchTicker failed:', e)
    return cacheGetStale<Ticker>('ticker:' + pair) || defaultTicker
  }
}

export async function fetchOrderBook(pair: string, levels = 15): Promise<OrderBook> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return { bids: [], asks: [] }
  }
  const marketSymbol = urlSymbolToMarketSymbol(pair)

  // Return cached if fresh
  const cached = cacheGet<OrderBook>('orderBook:' + pair)
  if (cached && (cached.bids.length > 0 || cached.asks.length > 0)) return cached

  try {
    const { data } = await tradingHttp.get('/api/crypto/exchange/books', {
      timeout: 15000,
      params: { marketSymbol, group: 0 },
    })

    // Backend returns bids/asks as [[price, amount], ...]
    const rawBids: [number, number][] = data.bids || []
    const rawAsks: [number, number][] = data.asks || []

    const bids: OrderBookEntry[] = rawBids.slice(0, levels).map(([price, amount]) => ({
      price: price.toString(),
      amount: amount.toString(),
    }))

    const asks: OrderBookEntry[] = rawAsks.slice(0, levels).map(([price, amount]) => ({
      price: price.toString(),
      amount: amount.toString(),
    }))

    const result = { bids, asks }
    cacheSet('orderBook:' + pair, result)
    return result
  } catch (e) {
    console.error('[tradingApi] fetchOrderBook failed:', e)
    return cacheGetStale<OrderBook>('orderBook:' + pair) || { bids: [], asks: [] }
  }
}

/**
 * In-flight oracle requests — prevents duplicate API calls when prefetch
 * and fetchOHLC race for the same pair.
 */
const oracleInflight = new Map<string, Promise<OHLCCandle[]>>()

/**
 * In-flight aggregated rate requests — deduplicates concurrent requests for the same pair.
 */
const aggregatedInflight = new Map<string, Promise<AggregatedExchangeRatesModel | null>>()

/**
 * Fetch full 1Y oracle history for a pair (cached for 30 min).
 * All oracle intervals (3M, 6M, 1Y) share this single cached dataset.
 * Deduplicates concurrent requests for the same pair.
 */
async function getOracleHistoryFull(pair: string, marketSymbol: string): Promise<OHLCCandle[]> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return []
  }
  const cacheKey = 'oracleRaw:' + pair

  // Return cached data if fresh
  const cached = cacheGet<OHLCCandle[]>(cacheKey)
  if (cached && cached.length > 0) {
    return cached
  }

  // If a request for this pair is already in flight, wait for it
  const inflight = oracleInflight.get(pair)
  if (inflight) {
    return inflight
  }

  // Start new request and track it
  const fetchPromise = (async () => {
    try {
      const { data } = await tradingHttp.get('/api/crypto/exchange/oracle-history', {
        timeout: 8000,
        params: { marketSymbol, interval: 'day', limit: 365 },
      })

      // Deduplicate by timestamp (Oracle may have multiple rows per day)
      const seen = new Set<number>()
      const candles: OHLCCandle[] = []
      for (const d of (data || [])) {
        if (!seen.has(d.timestamp)) {
          seen.add(d.timestamp)
          candles.push({
            timestamp: d.timestamp,
            open: d.open,
            high: d.high,
            low: d.low,
            close: d.close,
            volume: d.volume || 0,
          })
        }
      }

      // Sort by timestamp ascending (oldest first)
      candles.sort((a, b) => a.timestamp - b.timestamp)

      if (candles.length > 0) {
        cacheSet(cacheKey, candles)
        console.log(`[tradingApi] Oracle: fetched and cached ${candles.length} candles for ${pair}`)
      }

      return candles
    } finally {
      // Clean up in-flight tracker
      oracleInflight.delete(pair)
    }
  })()

  oracleInflight.set(pair, fetchPromise)
  return fetchPromise
}

/**
 * Fetch aggregated exchange rates for all short intervals (1H, 1D, 1W, 1M) in a single request.
 * Cached for 1 minute so interval switching is instant after first load.
 * Deduplicates concurrent requests for the same pair.
 */
export async function fetchAggregatedRates(pair: string): Promise<AggregatedExchangeRatesModel | null> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return null
  }
  const cacheKey = 'aggregatedRates:' + pair

  // Return cached data if fresh
  const cached = cacheGet<AggregatedExchangeRatesModel>(cacheKey)
  if (cached) return cached

  // If a request for this pair is already in flight, wait for it
  const inflight = aggregatedInflight.get(pair)
  if (inflight) return inflight

  // Split pair (e.g. "btc-usd") into from/to params
  const dashIdx = pair.indexOf('-')
  const from = dashIdx >= 0 ? pair.slice(0, dashIdx).toUpperCase() : pair.toUpperCase()
  const to = dashIdx >= 0 ? pair.slice(dashIdx + 1).toUpperCase() : 'USD'

  const fetchPromise = (async (): Promise<AggregatedExchangeRatesModel | null> => {
    try {
      const { data } = await tradingHttp.get('/api/crypto/v2/exchange/rates', {
        timeout: 8000,
        params: { from, to },
      })
      const result = data as AggregatedExchangeRatesModel
      cacheSet(cacheKey, result)
      return result
    } catch (e) {
      console.error('[tradingApi] fetchAggregatedRates failed:', e)
      return null
    } finally {
      aggregatedInflight.delete(pair)
    }
  })()

  aggregatedInflight.set(pair, fetchPromise)
  return fetchPromise
}

/**
 * Prefetch oracle history for a pair (call when user selects a trading pair).
 * This makes subsequent chart interval switches instant.
 */
export function prefetchOracleHistory(pair: string): void {
  const marketSymbol = urlSymbolToMarketSymbol(pair)
  getOracleHistoryFull(pair, marketSymbol).catch(() => {
    // Silently ignore prefetch errors
  })
  // Also warm the aggregated rates cache so short-interval switching is instant
  fetchAggregatedRates(pair).catch(() => {})
}

export async function fetchOHLC(pair: string, interval: ChartInterval, mode: 'candle' | 'line' = 'candle'): Promise<OHLCCandle[]> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return []
  }
  const marketSymbol = urlSymbolToMarketSymbol(pair)
  const targetCount = mode === 'line' ? LINE_COUNTS[interval] : CANDLE_COUNTS[interval]

  // Oracle for longer periods (daily candles only)
  // 3M, 6M, 1Y use Oracle (daily close); shorter intervals use Bitstamp rates
  const oracleIntervals: ChartInterval[] = ['3M', '6M', '1Y']
  if (oracleIntervals.includes(interval)) {
    try {
      // Fetch full 1Y data once and cache it, then slice locally for any interval
      const fullData = await getOracleHistoryFull(pair, marketSymbol)

      if (fullData.length > 0) {
        // Slice data based on requested interval
        const daysMap: Record<string, number> = { '3M': 90, '6M': 180, '1Y': 365 }
        const daysNeeded = daysMap[interval] || 365
        const slicedData = fullData.slice(-daysNeeded)

        console.log(`[tradingApi] ${interval} ${mode}: Oracle cache hit, sliced ${slicedData.length} from ${fullData.length} candles`)

        let candles: OHLCCandle[]
        if (mode === 'line') {
          // Use close price only — smooth line without OHLC peaks
          candles = slicedData.map(c => ({
            timestamp: c.timestamp,
            open: c.close,
            high: c.close,
            low: c.close,
            close: c.close,
            volume: c.volume || 0,
          }))
        } else {
          candles = aggregateCandles(slicedData, targetCount)
        }
        return candles
      }
    } catch { /* fallback to Bitstamp */ }
  }

  // 1H, 1D, 1W, 1M: use aggregated v2 endpoint (single cached request, all intervals)
  const aggregatedFieldMap: Partial<Record<ChartInterval, keyof AggregatedExchangeRatesModel>> = {
    '1H': 'rates1H',
    '1D': 'rates1D',
    '1W': 'rates1W',
    '1M': 'rates1M',
  }
  const aggregatedField = aggregatedFieldMap[interval]
  if (aggregatedField) {
    try {
      const aggregated = await fetchAggregatedRates(pair)
      if (aggregated) {
        let rates: ExchangeRateHistoryModel[] = aggregated[aggregatedField] || []

        // Pre-downsample large datasets before heavy processing
        if (rates.length > 500) {
          const step = Math.ceil(rates.length / 500)
          const sampled: ExchangeRateHistoryModel[] = []
          for (let i = 0; i < rates.length; i += step) sampled.push(rates[i])
          sampled.push(rates[rates.length - 1])
          rates = sampled
        }

        if (mode === 'line') {
          const sorted = removeStaleData(rates)
          const maxPoints = LINE_COUNTS[interval]
          const step = sorted.length > maxPoints ? Math.ceil(sorted.length / maxPoints) : 1
          const result: OHLCCandle[] = []
          for (let i = 0; i < sorted.length; i += step) {
            const r = sorted[i]
            result.push({
              timestamp: new Date(r.date).getTime(),
              open: r.rate, high: r.rate, low: r.rate, close: r.rate, volume: 0,
            })
          }
          if (sorted.length > 0) {
            const last = sorted[sorted.length - 1]
            if (step > 1 && result[result.length - 1]?.timestamp !== new Date(last.date).getTime()) {
              result.push({
                timestamp: new Date(last.date).getTime(),
                open: last.rate, high: last.rate, low: last.rate, close: last.rate, volume: 0,
              })
            }
          }
          cacheSet('ohlc:' + pair + ':' + interval, result)
          return result
        }

        const result = ratesToOHLC(rates, targetCount)
        cacheSet('ohlc:' + pair + ':' + interval, result)
        return result
      }
    } catch { /* fallback to per-interval fetch below */ }
  }

  // Fallback: per-interval fetch from /api/crypto/exchange/rates
  try {
    const { dateFrom, dateTo } = intervalToDateRange(interval)
    const { data } = await tradingHttp.get('/api/crypto/exchange/rates', {
      timeout: 8000,
      params: { marketSymbol, dateFrom, dateTo },
    })

    let rates: ExchangeRateHistoryModel[] = data || []

    // Pre-downsample large datasets before heavy processing
    if (rates.length > 500) {
      const step = Math.ceil(rates.length / 500)
      const sampled: ExchangeRateHistoryModel[] = []
      for (let i = 0; i < rates.length; i += step) sampled.push(rates[i])
      sampled.push(rates[rates.length - 1])
      rates = sampled
    }

    // Line chart: raw points, downsample if too many (skip every Nth)
    if (mode === 'line') {
      const sorted = removeStaleData(rates)
      const maxPoints = LINE_COUNTS[interval]
      const step = sorted.length > maxPoints ? Math.ceil(sorted.length / maxPoints) : 1
      const result: OHLCCandle[] = []
      for (let i = 0; i < sorted.length; i += step) {
        const r = sorted[i]
        result.push({
          timestamp: new Date(r.date).getTime(),
          open: r.rate, high: r.rate, low: r.rate, close: r.rate, volume: 0,
        })
      }
      if (sorted.length > 0) {
        const last = sorted[sorted.length - 1]
        if (step > 1 && result[result.length - 1].timestamp !== new Date(last.date).getTime()) {
          result.push({
            timestamp: new Date(last.date).getTime(),
            open: last.rate, high: last.rate, low: last.rate, close: last.rate, volume: 0,
          })
        }
      }
      cacheSet('ohlc:' + pair + ':' + interval, result)
      return result
    }

    const result = ratesToOHLC(rates, targetCount)
    cacheSet('ohlc:' + pair + ':' + interval, result)
    return result
  } catch (e) {
    console.error('[tradingApi] fetchOHLC failed:', e)
    return cacheGetStale<OHLCCandle[]>('ohlc:' + pair + ':' + interval) || []
  }
}

export async function fetchRecentTrades(pair: string, count = 30): Promise<Trade[]> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return []
  }
  const marketSymbol = urlSymbolToMarketSymbol(pair)

  try {
    const { data } = await tradingHttp.get('/api/crypto/exchange/trades', {
      timeout: 15000,
      params: { marketSymbol, time: 'day' },
    })

    const trades: ExchangeTradeModel[] = data.trades || data || []

    const result = trades.slice(0, count).map((t) => ({
      date: t.date,
      tid: t.tid,
      price: t.price.toString(),
      type: (t.type?.toLowerCase() === 'sell' ? '1' : '0') as '0' | '1',
      amount: t.amount.toString(),
    }))
    cacheSet('recentTrades:' + pair, result)
    return result
  } catch (e) {
    console.error('[tradingApi] fetchRecentTrades failed:', e)
    return cacheGetStale<Trade[]>('recentTrades:' + pair) || []
  }
}

export async function fetchTradeHistory(): Promise<TradeHistoryEntry[]> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return []
  }
  const cached = cacheGet<TradeHistoryEntry[]>('tradeHistory')
  if (cached) return cached

  try {
    const now = new Date()
    const threeMonthsAgo = new Date()
    threeMonthsAgo.setFullYear(threeMonthsAgo.getFullYear() - 1)

    const { data } = await tradingHttp.post('/api/crypto/exchange/orders', {
      dateFrom: threeMonthsAgo.toISOString(),
      dateTo: now.toISOString(),
      skip: 0,
      take: 50,
      orderStatuses: [],
      exchangeDirections: [],
      exchangeTypes: [],
      marketSymbol: '',
      currency: '',
      currencyBuy: '',
    })

    const orders: ExchangeOrderItemModel[] = data.data || data || []

    const result = orders.map((o) => {
      const side = o.exchangeDirection?.toLowerCase() === 'sell' ? 'sell' : 'buy'
      const base = o.currency?.toUpperCase() || ''
      const counter = o.buyCurrency?.toUpperCase() || ''
      const pairName = side === 'buy'
        ? `${counter}/${base}`
        : `${base}/${counter}`
      // For Buy: amount=USDC spent, buyAmount=XRP received
      // For Sell: amount=base sold, buyAmount=counter received
      const displayAmount = side === 'buy' ? o.buyAmount : o.amount
      const displayTotal = side === 'buy' ? o.amount : (o.buyAmount || o.amount * o.rate)

      return {
        date: o.orderDate,
        pair: pairName,
        type: (o.exchangeType || 'market').toLowerCase(),
        side: side as 'buy' | 'sell',
        price: parseFloat(o.rate.toString()).toFixed(2).padStart(5, '0'),
        amount: (() => {
          const amt = Math.max(parseFloat(displayAmount.toString()), 0)
          if (side === 'buy' && o.rate > 0 && amt === parseFloat(amt.toFixed(2))) {
            return (o.amount / o.rate).toFixed(6)
          }
          // Show up to 6 decimals, trim trailing zeros
          return parseFloat(amt.toFixed(6)).toString()
        })(),
        fee: (() => {
          // Use backend fee if available
          if (o.feeAmount && o.feeAmount > 0) {
            return `${parseFloat(o.feeAmount.toString()).toFixed(2)} ${o.feeCurrency || ''}`.trim()
          }
          // Calculate fee from order data: debit amount - (received × rate)
          const tradeValue = o.buyAmount * o.rate
          const calcFee = Math.abs(o.amount - tradeValue)
          return calcFee > 0.01 ? calcFee.toFixed(2) : '0'
        })(),
        total: parseFloat(displayTotal.toString()).toFixed(2).padStart(5, '0'),
        status: (o.orderStatus === 'Held' || o.orderStatus === 'HeldForReview') ? 'Open' : (o.orderStatus || 'Unknown'),
        orderId: o.referenceNumber || '',
      }
    })
    cacheSet('tradeHistory', result)
    return result
  } catch (e) {
    console.error('[tradingApi] fetchTradeHistory failed:', e)
    return cacheGetStale<TradeHistoryEntry[]>('tradeHistory') || []
  }
}

export async function fetchOpenOrders(): Promise<OpenOrder[]> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return []
  }
  const cacheKey = 'openOrders'
  const cached = cacheGet<OpenOrder[]>(cacheKey)
  if (cached) return cached

  try {
    // Fetch all trade history and filter for Held/Pending (open) orders
    const history = await fetchTradeHistory()
    const result = history
      .filter(h => h.status === 'Open')
      .map(h => ({
        date: h.date,
        pair: h.pair,
        type: h.type,
        side: h.side,
        price: h.price,
        amount: h.amount,
        filled: '0',
        total: h.total,
        orderId: h.orderId,
      }))
    cacheSet(cacheKey, result)
    return result
  } catch (e) {
    console.error('[tradingApi] fetchOpenOrders failed:', e)
    return cacheGetStale<OpenOrder[]>(cacheKey) || []
  }
}

// ── Order Placement ──

export interface CreateOrderParams {
  marketSymbol: string
  orderType: 'Market' | 'Limit' | 'StopLimit'
  orderDirection: 'Buy' | 'Sell'
  amount: number
  price?: number
  includedFees?: boolean
}

export interface DryRunParams {
  marketSymbol: string
  orderType: 'Market' | 'Limit' | 'StopLimit'
  orderDirection: 'Buy' | 'Sell'
  /** Forward calc: the amount the user is spending (debited side). */
  amount?: number
  /** Reverse calc: the target amount the user wants to RECEIVE (credited
   *  side). When set, the backend prices the spend and returns it as
   *  `debitedAmount`. Send either `amount` or `receivedAmount`, never both. */
  receivedAmount?: number
  price?: number
  includedFees?: boolean
}

export async function createOrder(params: CreateOrderParams): Promise<CreateOrderResponse> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    throw new Error('Crypto exchange is not permitted')
  }
  const body: any = {
    idempotentKey: crypto.randomUUID(),
    marketSymbol: params.marketSymbol,
    orderType: params.orderType,
    orderDirection: params.orderDirection,
    amount: params.amount,
    includedFees: params.includedFees ?? false,
  }
  if (params.price != null && params.price > 0) body.price = params.price
  const { data } = await tradingHttp.post('/api/crypto/exchange/order-create', body)
  return data
}

export async function dryRunOrder(params: DryRunParams): Promise<DryRunResponse> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    throw new Error('Crypto exchange is not permitted')
  }
  const body: any = {
    marketSymbol: params.marketSymbol,
    orderType: params.orderType,
    orderDirection: params.orderDirection,
    includedFees: params.includedFees ?? false,
  }
  // Forward calc drives off `amount` (spend); reverse calc drives off
  // `receivedAmount` (target receive). Send only the leg that drives the
  // calculation — the backend prices the other one.
  if (params.amount != null && params.amount > 0) body.amount = params.amount
  if (params.receivedAmount != null && params.receivedAmount > 0)
    body.receivedAmount = params.receivedAmount
  if (params.price != null && params.price > 0) body.price = params.price
  const { data } = await tradingHttp.post('/api/crypto/exchange/order-dry-run', body)
  return data
}

export async function cancelOrder(referenceNumber: string): Promise<void> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    throw new Error('Crypto exchange is not permitted')
  }
  await tradingHttp.post('/api/crypto/exchange/cancel-order', { referenceNumber })
}

// ── Account Balances ──

// Separate silent axios instance for account management — no error modals
const silentHttp = axios.create({
  baseURL: getTradingBaseURL(),
  headers: { 'x-api-key': CONFIG.VUE_APP_X_API_KEY },
})
silentHttp.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

/** Get list of existing account currencies for the client */
async function getExistingAccountCurrencies(): Promise<Set<string>> {
  if (!piniaHasPortalRight(B2bPortalRight.AccountView)) {
    return new Set()
  }
  try {
    const { data } = await silentHttp.get('/api/accounts')
    const accounts: any[] = data?.accounts || (Array.isArray(data) ? data : [])
    return new Set(accounts.map((a: any) => (a.currency || '').toUpperCase()).filter(Boolean))
  } catch {
    return new Set()
  }
}

/** Create an account for a currency if it doesn't exist (silent, no error on failure) */
async function ensureAccountExists(currency: string): Promise<void> {
  if (!piniaHasPortalRight(B2bPortalRight.AccountAdd)) {
    return
  }
  try {
    await silentHttp.post('/api/accounts', { currency: currency.toUpperCase() })
  } catch {
    // AccountAlreadyExists is expected — ignore all errors silently
  }
}

/** Ensure both base and counter currency accounts exist before placing an order */
export async function ensureTradingAccounts(base: string, counter: string): Promise<void> {
  const existing = await getExistingAccountCurrencies()
  const needed: string[] = []
  if (!existing.has(base.toUpperCase())) needed.push(base.toUpperCase())
  if (!existing.has(counter.toUpperCase())) needed.push(counter.toUpperCase())
  if (needed.length > 0) {
    await Promise.all(needed.map(c => ensureAccountExists(c)))
  }
}

/**
 * Fetch chart data for CryptoDetail page using backend APIs (scalable).
 * - 1W/1M/1Y: oracle-history (DB-cached daily candles)
 * - 1H/1D: Bitstamp exchange rates
 * Returns { timestamp, price } points for the CryptoDetail chart.
 */
export type CryptoInterval = '1H' | '1D' | '1W' | '1M' | '1Y'
export interface CryptoChartPoint { timestamp: number; price: number }

export async function fetchCryptoChart(symbol: string, interval: CryptoInterval, vsCurrency = 'USD'): Promise<CryptoChartPoint[]> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return []
  }
  const marketSymbol = `${symbol}${vsCurrency}`.toLowerCase()

  // 1H, 1D, 1W, 1M: use aggregated v2 endpoint (single cached request, all short intervals)
  const aggregatedFieldMap: Partial<Record<CryptoInterval, keyof AggregatedExchangeRatesModel>> = {
    '1H': 'rates1H',
    '1D': 'rates1D',
    '1W': 'rates1W',
    '1M': 'rates1M',
  }
  const aggregatedField = aggregatedFieldMap[interval]
  if (aggregatedField) {
    const pair = `${symbol.toLowerCase()}-${vsCurrency.toLowerCase()}`
    try {
      const aggregated = await fetchAggregatedRates(pair)
      if (aggregated) {
        const rates: ExchangeRateHistoryModel[] = aggregated[aggregatedField] || []
        const maxPoints = interval === '1H' ? 60 : 200
        const step = rates.length > maxPoints ? Math.ceil(rates.length / maxPoints) : 1
        const points: CryptoChartPoint[] = []
        for (let i = 0; i < rates.length; i += step) {
          const r = rates[i]
          if (r.rate > 0) {
            points.push({ timestamp: new Date(r.date).getTime(), price: r.rate })
          }
        }
        if (rates.length > 0 && step > 1) {
          const last = rates[rates.length - 1]
          if (last.rate > 0) {
            points.push({ timestamp: new Date(last.date).getTime(), price: last.rate })
          }
        }
        if (points.length > 0) return points
      }
    } catch { /* fallback to per-interval fetch below */ }
  }

  // 1Y: use oracle-history (DB-cached daily candles)
  if (interval === '1Y') {
    try {
      const { data } = await tradingHttp.get('/api/crypto/exchange/oracle-history', {
        timeout: 8000,
        params: { marketSymbol, interval: 'day', limit: 365 },
      })
      const seen = new Set<number>()
      const points: CryptoChartPoint[] = []
      for (const d of (data || [])) {
        if (!seen.has(d.timestamp) && d.close > 0) {
          seen.add(d.timestamp)
          points.push({ timestamp: d.timestamp, price: d.close })
        }
      }
      points.sort((a, b) => a.timestamp - b.timestamp)
      if (points.length > 0) return points
    } catch { /* fallback below */ }
  }

  // Fallback: per-interval fetch from /api/crypto/exchange/rates
  try {
    const now = new Date()
    const hoursMap: Record<string, number> = { '1H': 1, '1D': 24, '1W': 168, '1M': 720, '1Y': 8760 }
    const hours = hoursMap[interval] || 24
    const dateFrom = new Date(now.getTime() - hours * 60 * 60 * 1000).toISOString()
    const dateTo = now.toISOString()

    const { data } = await tradingHttp.get('/api/crypto/exchange/rates', {
      timeout: 8000,
      params: { marketSymbol, dateFrom, dateTo },
    })

    const rates: any[] = data || []
    // Downsample to max 200 points
    const maxPoints = interval === '1H' ? 60 : 200
    const step = rates.length > maxPoints ? Math.ceil(rates.length / maxPoints) : 1
    const points: CryptoChartPoint[] = []
    for (let i = 0; i < rates.length; i += step) {
      const r = rates[i]
      if (r.rate > 0) {
        points.push({ timestamp: new Date(r.date).getTime(), price: r.rate })
      }
    }
    // Ensure last point is included
    if (rates.length > 0 && step > 1) {
      const last = rates[rates.length - 1]
      if (last.rate > 0) {
        points.push({ timestamp: new Date(last.date).getTime(), price: last.rate })
      }
    }
    return points
  } catch {
    return []
  }
}

export interface CryptoPriceData {
  price: number
  change24h: number
}

/**
 * Fetch current price + 24h change for a crypto asset via backend APIs (scalable, no exposed keys).
 * Replaces coingecko.fetchPrice, which hit CryptoCompare directly from the browser.
 * - price: /api/crypto/exchange/current-rate
 * - change24h: derived from aggregated rate history (rates1D)
 */
export async function fetchCryptoPrice(symbol: string, vsCurrency = 'USD'): Promise<CryptoPriceData> {
  if (!piniaHasPortalRight(B2bPortalRight.CryptoExchange)) {
    return { price: 0, change24h: 0 }
  }
  const marketSymbol = `${symbol}${vsCurrency}`.toLowerCase()
  const pair = `${symbol.toLowerCase()}-${vsCurrency.toLowerCase()}`

  let price = 0
  let change24h = 0

  try {
    const { data } = await tradingHttp.get('/api/crypto/exchange/current-rate', {
      timeout: 15000,
      params: { marketSymbol },
    })
    price = (data as CurrentExchangeRateModel)?.rate || 0
  } catch { /* price unavailable */ }

  try {
    const aggregated = await fetchAggregatedRates(pair)
    const rates1D = aggregated?.rates1D
    if (rates1D?.length) {
      const price24hAgo = rates1D[0]?.rate || 0
      if (price24hAgo > 0 && price > 0) {
        change24h = ((price - price24hAgo) / price24hAgo) * 100
      }
    }
  } catch { /* change unavailable */ }

  return { price, change24h }
}

/**
 * Fetch 7-day sparkline close prices for multiple crypto symbols.
 * Uses the oracle-history backend (DB-cached, scales to any user count).
 * Returns { ETH: [price1, price2, ...], SOL: [...], ... }
 */
export async function fetchSparklines(symbols: string[], vsCurrency = 'USD'): Promise<Record<string, number[]>> {
  const result: Record<string, number[]> = {}
  const BATCH = 4
  const canUseOracle = piniaHasPortalRight(B2bPortalRight.CryptoExchange)

  for (let i = 0; i < symbols.length; i += BATCH) {
    const batch = symbols.slice(i, i + BATCH)
    await Promise.all(batch.map(async (sym) => {
      if (canUseOracle) {
        try {
          const marketSymbol = `${sym}${vsCurrency}`.toLowerCase()
          const { data } = await tradingHttp.get('/api/crypto/exchange/oracle-history', {
            timeout: 5000,
            params: { marketSymbol, interval: 'day', limit: 7 },
          })

          const entries: { ts: number; price: number }[] = []
          const seen = new Set<number>()
          for (const d of (data || [])) {
            if (!seen.has(d.timestamp) && d.close > 0) {
              seen.add(d.timestamp)
              entries.push({ ts: d.timestamp, price: d.close })
            }
          }
          entries.sort((a, b) => a.ts - b.ts)
          result[sym] = entries.map(e => e.price)
          return
        } catch { /* oracle-history unavailable */ }
      }
      result[sym] = []
    }))
  }

  return result
}

export async function fetchAccountBalances(): Promise<Record<string, number>> {
  if (!piniaHasPortalRight(B2bPortalRight.AccountView)) {
    return {}
  }
  try {
    const { data } = await silentHttp.get('/api/accounts')
    const balances: Record<string, number> = {}
    const accounts: any[] = data?.accounts || (Array.isArray(data) ? data : [])
    for (const a of accounts) {
      const currency = (a.currency || '').toUpperCase()
      const amount = parseFloat(a.amount ?? 0)
      if (!currency || !amount || currency.includes(':')) continue
      balances[currency] = (balances[currency] || 0) + amount
    }
    return balances
  } catch {
    return {}
  }
}
